import { NavLink } from 'react-router-dom'
import { FileText, Building2, CreditCard, Lock, ChevronRight } from 'lucide-react'

const features = [
  {
    icon: FileText,
    title: 'Digitale lejekontrakter',
    desc: 'A9 + A10 typeformular genereres automatisk fra dine lejemålsdata. Ingen Word-filer.',
  },
  {
    icon: Lock,
    title: 'MitID-signering',
    desc: 'Lejer og udlejer signerer digitalt med MitID. Juridisk bindende. Ingen printer.',
  },
  {
    icon: Building2,
    title: 'BBR-integration',
    desc: 'Ejendomsdata hentes automatisk fra BBR ved oprettelse. Aldrig mere manuel tastning.',
  },
  {
    icon: CreditCard,
    title: 'Huslejeopkrævning',
    desc: 'Månedlig fakturering og manuel betalingsregistrering. Bogføring eksporteres til e-conomic.',
  },
]

const testimonials = [
  {
    quote: 'Vi administrerer 47 lejemål. Boligflow sparer os 2-3 timer om måneden på varslingsskrivelser alene.',
    name: 'Mette K.',
    role: 'Privat udlejer, Aarhus',
  },
  {
    quote: 'Endelig et system der faktisk kender lejelovens regler. Konkurrenterne afleverer bare Word-filer.',
    name: 'Søren L.',
    role: 'Ejendomsadministrator, København',
  },
]

export default function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-navy-950 text-white py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
            Ejendomsadministration<br />
            <span className="text-navy-300">der virker.</span>
          </h1>
          <p className="text-navy-200 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Boligflow samler kontrakter, huslejeopkrævning, regulering og lejerkommunkation
            i ét system — bygget til dansk lejelovgivning fra dag ét.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://admin.boligflow.toragroup.dk"
              className="bg-white text-navy-950 font-semibold px-8 py-3 rounded-lg hover:bg-cream transition-colors inline-flex items-center gap-2 justify-center"
            >
              Kom i gang gratis <ChevronRight size={18} />
            </a>
            <NavLink
              to="/features"
              className="border border-navy-600 text-white font-semibold px-8 py-3 rounded-lg hover:border-navy-400 transition-colors inline-flex items-center gap-2 justify-center"
            >
              Se alle funktioner
            </NavLink>
          </div>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="bg-navy-900 py-5 px-6">
        <p className="text-center text-navy-400 text-sm">
          Bygget til <span className="text-white font-medium">danske udlejere</span> — privat, andels og erhverv
        </p>
      </section>

      {/* Features grid */}
      <section className="py-20 px-6 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-950 mb-4">Alt samlet ét sted</h2>
            <p className="text-navy-600 text-lg max-w-xl mx-auto">
              Fra lejekontrakt til fraflytningsopgørelse — Boligflow dækker hele lejemålets livscyklus.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-8 border border-navy-100">
                <div className="w-10 h-10 bg-navy-100 rounded-xl flex items-center justify-center mb-5">
                  <Icon size={20} className="text-navy-700" />
                </div>
                <h3 className="text-navy-950 font-semibold text-lg mb-2">{title}</h3>
                <p className="text-navy-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <NavLink
              to="/features"
              className="text-navy-700 font-semibold hover:text-navy-950 inline-flex items-center gap-1 transition-colors"
            >
              Se alle funktioner <ChevronRight size={16} />
            </NavLink>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-navy-950 text-center mb-12">Hvad siger udlejerne?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map(({ quote, name, role }) => (
              <blockquote key={name} className="bg-cream rounded-2xl p-8 border border-navy-100">
                <p className="text-navy-700 italic leading-relaxed mb-5">&ldquo;{quote}&rdquo;</p>
                <footer>
                  <p className="text-navy-950 font-semibold text-sm">{name}</p>
                  <p className="text-navy-500 text-sm">{role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-950 py-20 px-6 text-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-5">Klar til at spare tid?</h2>
          <p className="text-navy-300 text-lg mb-8">
            Start gratis. Ingen kreditkort. Opsæt dine første lejemål på under 10 minutter.
          </p>
          <a
            href="https://admin.boligflow.toragroup.dk"
            className="bg-white text-navy-950 font-semibold px-8 py-3 rounded-lg hover:bg-cream transition-colors inline-flex items-center gap-2"
          >
            Opret gratis konto <ChevronRight size={18} />
          </a>
        </div>
      </section>
    </>
  )
}
